import {
  BarangayTabulation,
  MunicipalTabulation,
  PrecinctTabulation,
  Tabulation,
  User,
  VoterTrackStats
} from '../../types';
import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { useNavigate, useParams } from 'react-router-dom';

import TabulationGraph from './TabulationGraph';
import { db } from '../../firebase';
import useAuth from '../../hooks/useAuth';

const BarangayStats: React.FC = () => {
  const { barangayId } = useParams<{ barangayId: string; }>();
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [tabulations, setTabulations] = useState<Tabulation[]>([]);
  const [exitPollsStats, setExitPollsStats] = useState<VoterTrackStats>();
  const [finalReleasesStats, setFinalReleasesStats] = useState<VoterTrackStats>();
  const [stubReleasesStats, setStubReleasesStats] = useState<VoterTrackStats>();
  const [barangayInfo, setBarangayInfo] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch stats from Firestore
  const fetchStatsFromFirestore = async () => {
    try {
      const exitPollsRef = doc(db, 'stats', 'exitPolls');
      const finalReleasesRef = doc(db, 'stats', 'finalReleases');
      const stubReleasesRef = doc(db, 'stats', 'stubReleases');

      const [exitPollsSnap, finalReleasesSnap, stubReleasesSnap] = await Promise.all([
        getDoc(exitPollsRef),
        getDoc(finalReleasesRef),
        getDoc(stubReleasesRef)
      ]);

      if (exitPollsSnap.exists()) {
        setExitPollsStats(exitPollsSnap.data() as VoterTrackStats);
      }
      if (finalReleasesSnap.exists()) {
        setFinalReleasesStats(finalReleasesSnap.data() as VoterTrackStats);
      }
      if (stubReleasesSnap.exists()) {
        setStubReleasesStats(stubReleasesSnap.data() as VoterTrackStats);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError('Failed to fetch stats');
    }
  };

  // Redirect if no view_throwing permission
  useEffect(() => {
    if (!loading && !user?.permissions.view_throwing) {
      navigate('/polls');
    }
  }, [user, loading, navigate]);

  const getTabulations = async (user: User, barangayId: string): Promise<void> => {
    try {
      const barangayDoc = doc(db, 'barangays', barangayId);
      const barangaySnap = await getDoc(barangayDoc);
      if (!barangaySnap.exists()) {
        console.error('Barangay not found');
        return;
      }
      const barangayInfo = barangaySnap.data();
      setBarangayInfo(barangayInfo);

      const barangayTabDoc = doc(
        db,
        `tabulationsV2/${user.dashboard}/regions/REGION XIII/provinces/SURIGAO DEL NORTE/districts`,
        barangayInfo.district,
        'cities',
        barangayInfo.city_municipality,
        'barangays',
        barangayInfo.barangay
      );
      const barangayTabSnap = await getDoc(barangayTabDoc);
      const barangayTab = barangayTabSnap.data() as MunicipalTabulation;

      const precinctTabs: Tabulation[] = [];
      const precinctTabsSnap = await getDocs(collection(barangayTabDoc, 'precincts'));
      precinctTabs.push(...precinctTabsSnap.docs.map((d) => d.data() as BarangayTabulation));

      const allTabulations = [
        barangayTab,
        ...precinctTabs
      ];
      setTabulations(allTabulations);
    } catch (err) {
      console.error('Error fetching tabulations:', err);
      setError('Failed to fetch tabulations');
    }
  };

  useEffect(() => {
    if (user && barangayId) {
      fetchStatsFromFirestore().then(() => {
        getTabulations(user, barangayId);
      });
    }
  }, [user, barangayId]);

  // If loading, return null to prevent flash of content
  if (loading) {
    return null;
  }
  
  return (
    <Box position="relative" height="100%" overflow="hidden">
      {error && (
        <Typography color="error" textAlign="center">
          {error}
        </Typography>
      )}
      {tabulations.length > 0 && stubReleasesStats && finalReleasesStats && exitPollsStats ? (
        <Grid container spacing={1}>
          {tabulations.map((tabulation) => (
            <Grid item xs={12} md={tabulation.type === 'barangay' ? 12 : 6} key={tabulation.id}>
              <TabulationGraph
                key={tabulation.id}
                tabulation={tabulation}
                stubReleasesStats={stubReleasesStats}
                finalReleasesStats={finalReleasesStats}
                exitPollsStats={exitPollsStats}
                height={tabulation.type === 'barangay' ? 300 : 250}
                navUrl={tabulation.type === 'precinct' 
                  ? `/precincts/${(tabulation as PrecinctTabulation).barangay}_${(tabulation as PrecinctTabulation).city_municipality}_${(tabulation as PrecinctTabulation).province}/${(tabulation as PrecinctTabulation).precinct}` 
                  : tabulation.type === 'barangay' && barangayInfo
                  ? `/barangay-voters?barangayName=${encodeURIComponent(barangayInfo.barangay)}&municipality=${encodeURIComponent(barangayInfo.city_municipality)}`
                  : undefined
                }
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default BarangayStats;
